/**
 * Combined export control for the Bank Transactions view.
 *
 * One "Export" button opens a popover where the practitioner picks the document
 * scope (all by default, or specific documents) and the format (Excel / Word /
 * PDF). Replaces the previous separate document multi-select + format dropdown.
 */

import { useMemo, useState } from "react";
import { saveAs } from "file-saver";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { exportToExcel, exportToWord, exportToPdf } from "@/utils/export";
import {
  buildBankTransactionsExport,
  type ExportTxn,
  type ExportAccount,
} from "./buildBankTransactionsExport";

type ExportFormat = "excel" | "word" | "pdf";

const FORMATS: Array<{
  key: ExportFormat;
  label: string;
  ext: string;
  icon: typeof FileText;
}> = [
  { key: "excel", label: "Excel", ext: "xlsx", icon: FileSpreadsheet },
  { key: "word", label: "Word", ext: "docx", icon: FileText },
  { key: "pdf", label: "PDF", ext: "pdf", icon: FileText },
];

const EXT_BY_FORMAT: Record<ExportFormat, string> = {
  excel: "xlsx",
  word: "docx",
  pdf: "pdf",
};

export function BankTransactionsExportButton({
  txns,
  accounts,
  documentOptions,
  isLoadingAll,
}: {
  txns: ExportTxn[];
  accounts: ExportAccount[];
  documentOptions: Array<{ value: string; label: string; title?: string }>;
  /** Transactions still streaming in — exporting now would produce a partial file. */
  isLoadingAll?: boolean;
}) {
  const [docFilter, setDocFilter] = useState<string[]>([]);
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  // Skip the (potentially 35k-row) build while pages are still streaming in —
  // the trigger is disabled then anyway.
  const content = useMemo(
    () =>
      isLoadingAll
        ? null
        : buildBankTransactionsExport(txns, accounts, docFilter),
    [txns, accounts, docFilter, isLoadingAll],
  );

  const handleExport = async (format: ExportFormat) => {
    if (!content) return;
    setBusy(format);
    try {
      const blob =
        format === "excel"
          ? exportToExcel(content)
          : format === "word"
            ? await exportToWord(content)
            : await exportToPdf(content);
      const ext = EXT_BY_FORMAT[format];
      const scope = docFilter.length > 0 ? `_${docFilter.length}_docs` : "";
      saveAs(blob, `Bank_Transactions${scope}.${ext}`);
    } catch (err) {
      console.error(`Bank transactions export (${format}) failed:`, err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={isLoadingAll}
          title={isLoadingAll ? "Transactions are still loading" : undefined}
        >
          <Download className="h-3.5 w-3.5" />
          {isLoadingAll ? "Loading…" : "Export"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        {documentOptions.length > 0 && (
          <>
            <div className="mb-1.5 text-xs font-medium text-foreground">
              Documents
            </div>
            <MultiSearchableSelect
              className="w-full"
              value={docFilter}
              onValueChange={setDocFilter}
              options={documentOptions}
              placeholder="All documents"
              searchPlaceholder="Search documents..."
              contentClassName="w-[16rem]"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Leave empty to export all documents.
            </p>
          </>
        )}
        <div className="mb-1.5 mt-3 text-xs font-medium text-foreground">
          Download as
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {FORMATS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={busy !== null}
              onClick={() => handleExport(f.key)}
            >
              {busy === f.key ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <f.icon className="h-3.5 w-3.5" />
              )}
              {f.label}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
